import { useCallback, useRef, useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import ParseEventStreamService from "../services/streamParser/streamParserService";
import type { StreamEvent } from "../services/streamParser/types";
import segmentRulesAgentService from "app/services/segments/segmentRulesAgentService";

import type { IRule } from "../types";
import { RuleTypes } from "../types";

export type AgentState = "idle" | "generating" | "done" | "error";

type UseSegmentRulesAgentReturn = {
    state: AgentState;
    streamedContent: string;
    generatedRules: IRule[];
    error: string | null;
    generate: (domain: string, segmentDescription: string, country?: string) => Promise<void>;
    cancel: () => void;
    reset: () => void;
};

const EMPTY_RULE: Omit<IRule, "type"> = { words: [], exact: [], folders: [], exactURLS: [] };

const toRule = (item: Record<string, unknown>): IRule => ({
    ...EMPTY_RULE,
    type: item.type === "exclude" || item.type === 1 ? RuleTypes.exclude : RuleTypes.include,
    words: Array.isArray(item.words) ? item.words.map(String) : [],
    exact: Array.isArray(item.exact) ? item.exact.map(String) : [],
    folders: Array.isArray(item.folders) ? item.folders.map(String) : [],
    exactURLS: Array.isArray(item.exactURLS ?? item.exactUrls)
        ? ((item.exactURLS ?? item.exactUrls) as unknown[]).map(String)
        : [],
});

const parseRulesFromStateEvent = (data: unknown): IRule[] => {
    if (!data || typeof data !== "object") return [];
    const state = data as Record<string, unknown>;
    const raw = state.segment_rules;
    if (!raw) return [];

    try {
        const rules = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!Array.isArray(rules)) return [];
        return rules.map((item: Record<string, unknown>) => toRule(item));
    } catch {
        return [];
    }
};

const parseRulesFromContent = (content: string): IRule[] => {
    try {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)```/) ?? content.match(/\[[\s\S]*\]/);
        const raw = jsonMatch ? (jsonMatch[1] ?? jsonMatch[0]) : content;
        const parsed = JSON.parse(raw.trim());

        if (!Array.isArray(parsed)) return [];

        return parsed.map((item: Record<string, unknown>) => toRule(item));
    } catch {
        return [];
    }
};

export const useSegmentRulesAgent = (): UseSegmentRulesAgentReturn => {
    const [state, setState] = useState<AgentState>("idle");
    const [streamedContent, setStreamedContent] = useState("");
    const [generatedRules, setGeneratedRules] = useState<IRule[]>([]);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const reset = useCallback(() => {
        setState("idle");
        setStreamedContent("");
        setGeneratedRules([]);
        setError(null);
    }, []);

    const cancel = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setState("idle");
    }, []);

    const generate = useCallback(async (domain: string, segmentDescription: string, country = "us") => {
        abortControllerRef.current?.abort();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setState("generating");
        setStreamedContent("");
        setGeneratedRules([]);
        setError(null);

        let accumulated = "";
        let stateRules: IRule[] = [];
        let hasStreamError = false;

        try {
            const { threadId } = await segmentRulesAgentService.createThread();

            if (abortController.signal.aborted) return;

            const { response } = (await segmentRulesAgentService.runAgent(
                threadId,
                { domain, segmentDescription, country },
                { cancellation: abortController.signal },
            )) as { response: Response };

            if (!response?.ok) {
                throw new Error(`Agent request failed: ${response?.status} ${response?.statusText}`);
            }

            await ParseEventStreamService.parseEventStream(response, {
                onEvent: (event: StreamEvent) => {
                    if (abortController.signal.aborted) return;

                    if (ParseEventStreamService.isErrorStreamEvent(event)) {
                        setError(event.data);
                        setState("error");
                        hasStreamError = true;
                        return;
                    }

                    if (event.type === "values") {
                        const parsed = parseRulesFromStateEvent(event.data);
                        if (parsed.length > 0) {
                            stateRules = parsed;
                        }
                        return;
                    }

                    if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
                        const content = event.data[0]?.content ?? "";
                        accumulated = content;
                        setStreamedContent(content);
                    }
                },
            });

            if (abortController.signal.aborted || hasStreamError) return;

            const rules = stateRules.length > 0 ? stateRules : parseRulesFromContent(accumulated);
            setGeneratedRules(rules);
            setState(rules.length > 0 ? "done" : "error");

            if (rules.length === 0) {
                setError("Could not extract rules from the AI response. Try a different description.");
            }
        } catch (e) {
            if ((e as Error).name === "AbortError") return;

            Sentry.error("SegmentRulesAgent failed", e);
            setError((e as Error).message ?? "An unexpected error occurred.");
            setState("error");
        }
    }, []);

    return { state, streamedContent, generatedRules, error, generate, cancel, reset };
};
